import { Box } from '@mui/material'
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { styled, useTheme } from '@mui/material/styles'
import { useState } from 'react'
import { ITooltipData } from '../../definitions/Tooltip'
import TooltipsManager from '../../managers/TooltipsManager'

export const tooltipCache = new Map<string, ITooltipData>()

interface SmartTooltipProps {
    children: JSX.Element | JSX.Element[] | string
    tooltipKey?: string
}

function SmartTooltip({ children, tooltipKey }: SmartTooltipProps) {
    const theme = useTheme()
    const [tooltipData, setTooltipData] = useState<ITooltipData>(null)
    const [loading, setLoading] = useState(false)

    const handleTooltipOpen = async () => {
        if (!tooltipKey || loading) return

        if (tooltipCache.has(tooltipKey)) {
            setTooltipData(tooltipCache.get(tooltipKey))
            return
        }

        setLoading(true)
        try {
            const data: ITooltipData = await TooltipsManager.getTooltipByKey(tooltipKey)
            const tooltip = Array.isArray(data) && data.length > 0 ? data[0] : null

            tooltipCache.set(tooltipKey, tooltip)
            setTooltipData(tooltip)
        } catch (error) {
            console.error('Error fetching tooltip data:', error)
            setTooltipData(null)
        } finally {
            setLoading(false)
        }
    }

    const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
        <Tooltip {...props} classes={{ popper: className }} />
    ))(({ theme }) => ({
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: 'rgba(53,141,167, 0.5)',
            backdropFilter: 'blur(10px)',
            minWidth: 500,
            fontSize: theme.typography.pxToRem(12),
            border: '1px solid #65a1b3',
            borderRadius: theme.shape.borderRadius * 3
        }
    }))

    const tooltipContent = tooltipData
        ? (
                <Box sx={{ padding: theme.spacing(2) }}>
                    <Typography variant='subtitle1' fontSize={18} fontWeight='bold' gutterBottom>
                        {tooltipData.title}
                    </Typography>
                    <Typography fontSize={15} lineHeight={1.5} variant='body2'>
                        {tooltipData.description}
                    </Typography>
                </Box>
            )
        : loading
            ? (
                    <Box sx={{ padding: theme.spacing(2) }}>Loading...</Box>
                )
            : (
                    <Box sx={{ padding: theme.spacing(2) }}>No data available</Box>
                )

    if (!tooltipKey) return <Box>{children}</Box>

    return (
        <CustomTooltip
            title={tooltipContent}
            onOpen={handleTooltipOpen}
            placement='bottom'
            disableInteractive>
            <Box>{children}</Box>
        </CustomTooltip>
    )
}

export default SmartTooltip
